import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useGoalCreateMutation } from "web_myplann/Api/Goals/useGoalCreateMutation";
import { useGoalUpdateMutation } from "web_myplann/Api/Goals/useGoalUpdateMutation";
import {
	GOAL_AMOUNT_MAX,
	GOAL_DEADLINE_MAX_YEARS,
	GOAL_MONTHLY_RENT_MAX,
	GOAL_NAME_MAX_LENGTH,
	GOAL_RENT_YEARS_MAX,
	GOAL_RENT_YEARS_MIN,
} from "web_myplann/Goals/constants";
import { getAmountMaxMessage } from "web_myplann/Goals/Helpers/getAmountMaxMessage";
import { getMaxGoalDueDate } from "web_myplann/Goals/Helpers/getMaxGoalDueDate";
import { getMinGoalDueDate } from "web_myplann/Goals/Helpers/getMinGoalDueDate";
import { isRentBasedCategory } from "web_myplann/Goals/Helpers/goalCategory";
import { mapGoalFormToCreateRequest } from "web_myplann/Goals/Mappers/mapGoalFormToCreateRequest";
import { mapGoalFormToUpdateRequest } from "web_myplann/Goals/Mappers/mapGoalFormToUpdateRequest";
import { RentDrawdownMode } from "web_myplann/Goals/Types/RentDrawdownMode";
import {
	GoalCategoryTypeEnum,
	type InspirationTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { dateInMonthRange } from "web_myplann/Shared/Validation/dateInMonthRange";
import { z } from "zod";

const getGoalFormSchema = (
	t: TranslateFunction,
	minDueDate: Date,
	maxDueDate: Date,
) => {
	const amountMaxMessage = getAmountMaxMessage(t, GOAL_AMOUNT_MAX);
	const monthlyRentMaxMessage = getAmountMaxMessage(t, GOAL_MONTHLY_RENT_MAX);

	return z
		.object({
			category: z.nativeEnum(GoalCategoryTypeEnum, {
				required_error: t(Resources.Form.Validations.required),
			}),
			name: z
				.string({
					required_error: t(Resources.Form.Validations.required),
				})
				.trim()
				.min(1, t(Resources.Form.Validations.required))
				.max(
					GOAL_NAME_MAX_LENGTH,
					t(Resources.Goals.Form.nameMaxLength, { max: GOAL_NAME_MAX_LENGTH }),
				),
			dueDate: dateInMonthRange(
				z.date({
					required_error: t(Resources.Form.Validations.required),
					invalid_type_error: t(Resources.Form.Validations.required),
				}),
				minDueDate,
				maxDueDate,
				{
					tooSoonMessage: t(Resources.Goals.Form.Validations.dueDateTooSoon),
					tooFarMessage: t(Resources.Goals.Form.Validations.dueDateTooFar, {
						years: GOAL_DEADLINE_MAX_YEARS,
					}),
				},
			),
			totalValue: z
				.number({
					required_error: t(Resources.Form.Validations.required),
					invalid_type_error: t(Resources.Form.Validations.required),
				})
				.positive(t(Resources.Goals.Form.Validations.amountPositive))
				.max(GOAL_AMOUNT_MAX, amountMaxMessage),
			ownFunds: z
				.number()
				.min(0)
				.max(GOAL_AMOUNT_MAX, amountMaxMessage)
				.nullish(),
			loanAmount: z
				.number()
				.min(0)
				.max(GOAL_AMOUNT_MAX, amountMaxMessage)
				.nullish(),
			monthlyRent: z
				.number()
				.positive(t(Resources.Goals.Form.Validations.amountPositive))
				.max(GOAL_MONTHLY_RENT_MAX, monthlyRentMaxMessage)
				.nullish(),
			rentYears: z.number().min(0).nullish(),
			drawdownMode: z.nativeEnum(RentDrawdownMode).optional(),
			statePension: z
				.number()
				.min(0)
				.max(GOAL_MONTHLY_RENT_MAX, monthlyRentMaxMessage)
				.nullish(),
		})
		.superRefine((data, ctx) => {
			if (
				data.drawdownMode !== RentDrawdownMode.ToZero ||
				!isRentBasedCategory(data.category)
			)
				return;

			const years = data.rentYears ?? 0;
			if (years >= GOAL_RENT_YEARS_MIN && years <= GOAL_RENT_YEARS_MAX) return;

			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["rentYears"],
				message: t(Resources.Goals.Form.Validations.rentYearsRange, {
					min: GOAL_RENT_YEARS_MIN,
					max: GOAL_RENT_YEARS_MAX,
				}),
			});
		});
};

export type GoalFormModel = z.infer<ReturnType<typeof getGoalFormSchema>>;

type UseGoalFormProps = {
	goalId?: number;
	initialValues?: Partial<GoalFormModel>;
	inspirationType?: InspirationTypeEnum;
	onSuccess?: (goalId?: number) => void;
};

export const getDefaultDueDate = () => {
	const now = new Date();
	return new Date(now.getFullYear() + 5, now.getMonth(), 1);
};

export const useGoalForm = ({
	goalId,
	initialValues,
	inspirationType,
	onSuccess,
}: UseGoalFormProps) => {
	const { t } = useResource();
	const { mutate: createGoal, isPending: isCreating } = useGoalCreateMutation();
	const { mutate: updateGoal, isPending: isUpdating } = useGoalUpdateMutation();

	const minDueDate = useMemo(() => getMinGoalDueDate(), []);
	const maxDueDate = useMemo(() => getMaxGoalDueDate(), []);
	const resolver = useMemo(
		() => zodResolver(getGoalFormSchema(t, minDueDate, maxDueDate)),
		[t, minDueDate, maxDueDate],
	);

	const form = useForm<GoalFormModel>({
		resolver,
		mode: "onChange",
		defaultValues: {
			category: undefined,
			name: "",
			dueDate: getDefaultDueDate(),
			ownFunds: 0,
			loanAmount: 0,
			drawdownMode: RentDrawdownMode.ToZero,
		},
	});

	const appliedInitialRef = useRef(false);
	useEffect(() => {
		if (appliedInitialRef.current || !initialValues) return;
		const defined = Object.fromEntries(
			Object.entries(initialValues).filter(([, v]) => v !== undefined),
		) as Partial<GoalFormModel>;
		if (Object.keys(defined).length === 0) return;
		appliedInitialRef.current = true;
		form.reset(
			{ ...form.getValues(), ...defined },
			{ keepDefaultValues: true },
		);
	}, [form, initialValues]);

	const submit = form.handleSubmit((data) => {
		if (goalId != null) {
			updateGoal(
				{ id: goalId, body: mapGoalFormToUpdateRequest(data) },
				{ onSuccess: () => onSuccess?.(goalId) },
			);
			return;
		}
		createGoal(mapGoalFormToCreateRequest(data, inspirationType), {
			onSuccess: (response) => onSuccess?.(response.id ?? undefined),
		});
	});

	return {
		...form,
		submit,
		isSubmitting: isCreating || isUpdating,
		minDueDate,
		maxDueDate,
	};
};

export type UseGoalFormReturn = ReturnType<typeof useGoalForm>;
